import { supabase } from "@/integrations/supabase/client";

export interface NotificationPermissionResult {
  granted: boolean;
  supported: boolean;
  message: string;
}

export interface ScheduledNotification {
  id: string;
  title: string;
  body: string;
  scheduledTime: Date;
  doseId: string;
  medicationName: string;
}

class NotificationService {
  private isSupported: boolean;
  private isIOS: boolean;
  private scheduledNotifications: Map<string, number> = new Map();

  constructor() {
    this.isSupported = "Notification" in window && "serviceWorker" in navigator;
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  async requestPermission(): Promise<NotificationPermissionResult> {
    if (!this.isSupported) {
      return {
        granted: false,
        supported: false,
        message: "Notificações não são suportadas neste navegador",
      };
    }

    try {
      const permission = await Notification.requestPermission();
      
      return {
        granted: permission === "granted",
        supported: true,
        message: permission === "granted" 
          ? "Permissão concedida! Você receberá alarmes de medicação."
          : permission === "denied"
          ? "Permissão negada. Ative notificações nas configurações do navegador."
          : "Permissão pendente. Por favor, autorize as notificações.",
      };
    } catch (error) {
      console.error("Erro ao solicitar permissão:", error);
      return {
        granted: false,
        supported: true,
        message: "Erro ao solicitar permissão de notificações",
      };
    }
  }

  async scheduleNotification(notification: ScheduledNotification): Promise<void> {
    if (!this.isSupported || Notification.permission !== "granted") {
      return;
    }

    const now = new Date().getTime();
    const scheduledTime = notification.scheduledTime.getTime();
    const delay = scheduledTime - now;

    if (delay <= 0) {
      return;
    }

    // Cancelar notificação anterior se existir
    this.cancelNotification(notification.doseId);

    // Agendar nova notificação
    const timeoutId = window.setTimeout(() => {
      this.showNotification(notification);
    }, delay);

    this.scheduledNotifications.set(notification.doseId, timeoutId);
  }

  private async showNotification(notification: ScheduledNotification): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.ready;
      
      await registration.showNotification(notification.title, {
        body: notification.body,
        icon: "/icons/icon-192x192.png",
        badge: "/icons/icon-72x72.png",
        tag: `medication-${notification.doseId}`,
        requireInteraction: true,
        actions: this.isIOS ? [] : [
          { action: "confirm", title: "✓ Confirmar" },
          { action: "snooze", title: "⏰ Lembrar em 10min" }
        ],
        data: {
          doseId: notification.doseId,
          medicationName: notification.medicationName,
          url: `/dashboard?dose=${notification.doseId}`
        },
        vibrate: [200, 100, 200, 100, 200, 100, 200]
      } as NotificationOptions & { vibrate?: number[] });

      // Tocar som de alarme (funciona apenas quando app está aberto)
      this.playAlarmSound();
    } catch (error) {
      console.error("Erro ao exibir notificação:", error);
    }
  }

  private playAlarmSound(): void {
    try {
      const audio = new Audio("/sounds/alarm.mp3");
      audio.volume = 0.7;
      audio.play().catch(err => console.warn("Não foi possível tocar o alarme:", err));
    } catch (error) {
      console.warn("Alarme sonoro não disponível");
    }
  }

  cancelNotification(doseId: string): void {
    const timeoutId = this.scheduledNotifications.get(doseId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.scheduledNotifications.delete(doseId);
    }
  }

  async syncAllMedications(): Promise<void> {
    if (Notification.permission !== "granted") {
      return;
    }

    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      // Cancelar todas as notificações anteriores
      this.cancelAllNotifications();

      // Buscar próximas 24 horas de doses
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const { data: doses, error } = await supabase
        .from("medication_doses")
        .select(`
          id,
          scheduled_time,
          status,
          medications (
            name,
            dosage,
            doses_per_intake
          )
        `)
        .gte("scheduled_time", now.toISOString())
        .lt("scheduled_time", tomorrow.toISOString())
        .eq("status", "pending");

      if (error) throw error;

      let scheduledCount = 0;
      doses?.forEach((dose: any) => {
        const scheduledTime = new Date(dose.scheduled_time);
        
        this.scheduleNotification({
          id: dose.id,
          title: "⏰ Hora do Remédio",
          body: `${dose.medications.doses_per_intake || 1}x ${dose.medications.name} ${dose.medications.dosage}`,
          scheduledTime,
          doseId: dose.id,
          medicationName: dose.medications.name
        });
        scheduledCount++;
      });

      console.log(`📅 ${scheduledCount} lembretes agendados para as próximas 24h`);
    } catch (error) {
      console.error("Erro ao sincronizar medicações:", error);
    }
  }

  async scheduleTodayDoses(): Promise<void> {
    return this.syncAllMedications();
  }

  cancelAllNotifications(): void {
    this.scheduledNotifications.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.scheduledNotifications.clear();
  }

  getPermissionStatus(): string {
    if (!this.isSupported) return "not-supported";
    return Notification.permission;
  }

  isIOSDevice(): boolean {
    return this.isIOS;
  }
}

export const notificationService = new NotificationService();
